import { FINANCE_JOURNEY, CASH_JOURNEY } from 'src/types/journey';
import { ICarDetail } from 'src/redux/carDetails/carDetails.duck.interface';
import { IDeal } from 'src/interfaces/Car';
import { createDeal } from '@utils/Car.utils';
import { useCallback } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { Redux, CarDetailsDuck, FinanceWidgetDuck } from 'src/redux';
import { useExternalCheckout } from '@hooks/useExternalCheckout';
import { useJourneyType } from '@hooks/useJourneyType';
import { getGeolocationExtraFileds } from '@utils/Deal.utils';
import { FinanceStatuses } from 'src/types/financeStatuses';

const useCore = () => {
    const carDetails: ICarDetail = useSelector((state: Redux) => CarDetailsDuck.getCurrentCarDetails(state));
    const { paymentJourneyType, carJourney } = useJourneyType();
    const financeWidgetParameters = useSelector((state: Redux) => FinanceWidgetDuck.getFinanceWidgetParameters(state));
    const { externalCheckoutEnabled } = useExternalCheckout();
    const currentDeal: IDeal = useSelector((state: Redux) => CarDetailsDuck.getCurrentDeal(state));
    const dispatch = useDispatch();
    const configId = carDetails?._id ?? carDetails?.externalId;
    const samePaymentJourney =
        !currentDeal?.extraFields?.hasSelectedPaymentJourney ||
        (paymentJourneyType === 'finance' && currentDeal?.journeyType === FINANCE_JOURNEY) ||
        (paymentJourneyType === 'cash' && currentDeal?.journeyType === CASH_JOURNEY);
    const sameDealExists = currentDeal?.configuration === configId && samePaymentJourney;

    const extra = {
        financeGatewayParameters: financeWidgetParameters ?? {},
        useExternalCheckout: externalCheckoutEnabled,
        ...getGeolocationExtraFileds(currentDeal),
        ...(currentDeal?.extraFields?.hasSelectedPaymentJourney && {
            hasSelectedPaymentJourney: currentDeal?.extraFields.hasSelectedPaymentJourney,
        }),
        ...(currentDeal?.extraFields?.selectedFeesValue && {
            selectedFeesValue: currentDeal?.extraFields.selectedFeesValue,
        }),
        ...(currentDeal?.extraFields?.selectedPxValue && {
            selectedFeesValue: currentDeal?.extraFields.selectedPxValue,
        }),
    };

    return {
        carDetails,
        paymentJourneyType,
        carJourney,
        financeWidgetParameters,
        externalCheckoutEnabled,
        currentDeal,
        dispatch,
        configId,
        samePaymentJourney,
        sameDealExists,
        extra,
    };
};

export const useDealCreate = (): { dealCreate: (status: FinanceStatuses) => Promise<IDeal> } => {
    const {
        carDetails,
        paymentJourneyType,
        financeWidgetParameters,
        externalCheckoutEnabled,
        currentDeal,
        configId,
        carJourney,
        dispatch,
        sameDealExists,
        extra,
    } = useCore();

    return {
        dealCreate: useCallback(
            async (status: FinanceStatuses) => {
                if (sameDealExists) {
                    return currentDeal;
                }
                const { deal, financeQuote } = await createDeal(carDetails, paymentJourneyType, {
                    ...extra,
                    status,
                });
                batch(() => {
                    dispatch(CarDetailsDuck.setCurrentDealAndCarDetail(deal));
                    dispatch(FinanceWidgetDuck.setFinanceQuote(financeQuote));
                });
                return deal;
            },
            [configId, sameDealExists, paymentJourneyType, carJourney, financeWidgetParameters, externalCheckoutEnabled]
        ),
    };
};

export const useCreateDeal: () => () => Promise<IDeal> = () => {
    const {
        carDetails,
        paymentJourneyType,
        carJourney,
        financeWidgetParameters,
        externalCheckoutEnabled,
        currentDeal,
        dispatch,
        configId,
        sameDealExists,
        extra,
    } = useCore();

    return useCallback(async () => {
        if (sameDealExists) {
            return currentDeal;
        }
        const { deal } = await createDeal(carDetails, paymentJourneyType, extra);
        dispatch(CarDetailsDuck.setCurrentDealAndCarDetail(deal));
        return deal;
    }, [configId, sameDealExists, paymentJourneyType, carJourney, financeWidgetParameters, externalCheckoutEnabled]);
};
